import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, ClubWithStats, ClothingItem } from '../types';
import { Ionicons } from '@expo/vector-icons';
import { AppHeader, ScreenLayout, ItemCard, ClubLogoUploader, ClubLogo, ClubEditModal, ClubMark, SectionHeader, EmptyState, Button, PageTitle } from '../components';
import { Colors, Fonts, Radii, Shadows, Spacing, Typography, StatusTints } from '../constants';
import { clubService } from '../services/club';
import { authService } from '../services/auth';
import { adminService } from '../services/admin';
import { useResponsive } from '../utils/responsive';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ClubDetail'>;

export const ClubDetailScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { clubId } = route.params;
  const [club, setClub] = useState<ClubWithStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [listings, setListings] = useState<ClothingItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [canEditClub, setCanEditClub] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { isDesktop } = useResponsive();

  const loadClubData = async () => {
    try {
      setLoading(true);
      const user = await authService.getCurrentUser();
      setUserId(user?.id || null);

      const clubData = await clubService.getClubById(clubId);
      if (!clubData) {
        dialog.alert(t('common.errors.title'), t('screens.clubDetail.clubNotFound'), 'error');
        navigation.goBack();
        return;
      }
      setClub(clubData);

      if (user) {
        const memberStatus = await clubService.isUserMember(clubId, user.id);
        setIsMember(memberStatus);

        // Check if user can edit club (platform admin or club manager)
        const isAdmin = user.is_platform_admin || false;
        setIsUserAdmin(isAdmin);
        const isManager = await adminService.isUserClubManager(user.id, clubId);
        setCanEditClub(isAdmin || isManager);
      }

      const clubListings = await clubService.getClubListings(clubId, 5);
      setListings(clubListings);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.clubDetail.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClubData();
  }, [clubId]);

  const handleJoinLeave = async () => {
    if (!userId) {
      dialog.alert(t('common.errors.title'), t('screens.clubDetail.notAuthenticated'), 'error');
      return;
    }

    if (isMember && club) {
      const confirmed = await dialog.confirm(
        t('screens.clubDetail.leaveConfirmTitle', { club: club.name }),
        t('screens.clubDetail.leaveConfirmMessage'),
        { variant: 'warning' }
      );
      if (!confirmed) return;
    }

    try {
      setActionLoading(true);
      if (isMember) {
        await clubService.leaveClub(clubId);
        setIsMember(false);
        dialog.alert(t('screens.register.success'), t('screens.clubDetail.leaveSuccess'), 'success');
      } else {
        await clubService.joinClub(clubId);
        setIsMember(true);
        dialog.alert(t('screens.register.success'), t('screens.clubDetail.joinSuccess'), 'success');
      }
      // Reload to update stats
      loadClubData();
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), error.message || (isMember ? t('screens.clubDetail.leaveFailed') : t('screens.clubDetail.joinFailed')), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogoUpdate = async (publicId: string | null) => {
    try {
      await clubService.updateClub(clubId, { logo_url: publicId });
      dialog.alert(
        t('screens.register.success'),
        t('screens.clubDetail.logoUpdated'),
        'success'
      );
      loadClubData(); // Reload to show updated logo
    } catch (error: any) {
      dialog.alert(
        t('common.errors.title'),
        error.message || t('screens.clubDetail.logoUpdateFailed'),
        'error'
      );
    }
  };

  const handleSaveEdit = async (updates: Partial<ClubWithStats>) => {
    if (!club) return;

    try {
      await clubService.updateClub(clubId, updates);
      await dialog.alert(
        t('screens.register.success'),
        t('screens.manageClubs.successUpdate'),
        'success'
      );
      setShowEditModal(false);
      await loadClubData(); // Reload to show updated data
    } catch (error: any) {
      let errorMessage = error.message;
      if (errorMessage.includes('already exists')) {
        errorMessage = t('screens.manageClubs.errorDuplicateName');
      }
      throw error; // Re-throw for ClubEditModal to handle
    }
  };

  const renderHeader = () =>
    !isDesktop ? (
      <AppHeader
        navigation={navigation}
        currentScreen="ClubDetail"
        title={t('screens.clubDetail.title')}
        showBackButton={true}
      />
    ) : null;

  if (loading) {
    return (
      <ScreenLayout fullWidth={true}>
        <View style={styles.container}>
          {renderHeader()}
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={Colors.primary} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  if (!club) {
    return (
      <ScreenLayout fullWidth={true}>
        <View style={styles.container}>
          {renderHeader()}
          <View style={styles.centerContainer}>
            <Text style={styles.errorText}>{t('screens.clubDetail.clubNotFoundMessage')}</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const mobile = !isDesktop;
  const joinLeaveButton = isMember ? (
    <Button
      title={t('screens.clubDetail.leaveClub')}
      variant="ghost"
      onPress={handleJoinLeave}
      loading={actionLoading}
      style={mobile ? styles.fullWidth : undefined}
      testID="club-leave"
    />
  ) : (
    <Button
      title={t('screens.clubDetail.joinClub')}
      icon="add"
      onPress={handleJoinLeave}
      loading={actionLoading}
      style={mobile ? styles.fullWidth : undefined}
      testID="club-join"
    />
  );

  const stats: Array<[number, string]> = [
    [club.member_count, t('screens.clubDetail.members')],
    [club.listing_count, t('screens.clubDetail.listings')],
    [club.manager_count, t('screens.clubDetail.managers')],
  ];

  return (
    <ScreenLayout desktopPadding>
      <View style={styles.container}>
        {renderHeader()}

        <ScrollView style={styles.content} contentContainerStyle={styles.scrollContentContainer}>
          <View style={[styles.contentContainer, mobile && styles.contentContainerMobile]}>
            {isDesktop && (
              <PageTitle
                title={t('screens.clubDetail.title')}
                onBack={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Clubs')}
                right={
                  <Button
                    title={t('screens.clubLeaderboard.title')}
                    variant="ghost"
                    size="sm"
                    icon="trophy-outline"
                    onPress={() => navigation.navigate('ClubLeaderboard')}
                  />
                }
              />
            )}

            {/* Club card: linen header, stats row, join/leave */}
            <View style={styles.clubCard}>
              <View style={[styles.clubHeader, mobile && styles.clubHeaderMobile]}>
                {club.logo_url ? (
                  <ClubLogo logoUrl={club.logo_url} size={mobile ? 64 : 80} borderRadius={Radii.card} />
                ) : (
                  <ClubMark name={club.name} size={mobile ? 64 : 80} />
                )}
                <View style={styles.clubInfo}>
                  <View style={styles.clubNameRow}>
                    <Text style={[Typography.section, mobile && styles.clubNameMobile, styles.clubName]}>{club.name}</Text>
                    {club.is_verified && (
                      <View style={styles.verifiedTag}>
                        <Ionicons name="checkmark-circle" size={12} color={StatusTints.success.fg} />
                        <Text style={styles.verifiedText}>{t('screens.clubDetail.verified')}</Text>
                      </View>
                    )}
                  </View>
                  {(club.location || club.description) && (
                    <Text style={styles.clubDescription}>
                      {[club.location, club.description].filter(Boolean).join(' — ')}
                    </Text>
                  )}
                </View>
                {!mobile && joinLeaveButton}
              </View>
              <View style={styles.statsRow}>
                {stats.map(([n, label], i) => (
                  <View key={label} style={[styles.statCell, i > 0 && styles.statCellBordered]}>
                    <Text style={styles.statValue}>{n}</Text>
                    <Text style={[Typography.overline, styles.statLabel]}>{label}</Text>
                  </View>
                ))}
              </View>
              {mobile && <View style={styles.mobileAction}>{joinLeaveButton}</View>}
            </View>

            {/* Manager tools */}
            {canEditClub && (
              <View style={[styles.managerTools, mobile && styles.managerToolsMobile]}>
                <ClubLogoUploader
                  currentLogoUrl={club.logo_url}
                  onLogoUpdated={handleLogoUpdate}
                />
                <Button
                  title={t('screens.clubDetail.editClub')}
                  icon="create-outline"
                  variant="dark"
                  onPress={() => setShowEditModal(true)}
                  style={mobile ? styles.fullWidth : undefined}
                />
              </View>
            )}

            {/* Recent listings */}
            <View style={styles.listingsSection}>
              <SectionHeader
                mobile={mobile}
                title={t('screens.clubDetail.recentListings')}
                count={listings.length}
                action={listings.length ? t('screens.clubDetail.viewAllListings') : undefined}
                onAction={() => navigation.navigate('Browse', { club_id: club.id })}
              />
              {listings.length === 0 ? (
                <EmptyState
                  title={t('screens.clubDetail.noListings')}
                  subtitle={t('screens.clubDetail.noListingsSubtext')}
                  actionLabel={isMember ? t('screens.home.addFirstItem') : undefined}
                  onAction={isMember ? () => navigation.navigate('ItemForm') : undefined}
                />
              ) : (
                <View style={[styles.listingsGrid, !mobile && styles.listingsGridDesktop]}>
                  {listings.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      square={mobile}
                      onPress={() => navigation.navigate('ItemDetails', { itemId: item.id })}
                      style={mobile ? styles.gridItemMobile : styles.gridItemDesktop}
                    />
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>

        {/* Edit Club Modal */}
        {club && (
          <ClubEditModal
            visible={showEditModal}
            club={club}
            isAdmin={isUserAdmin}
            onClose={() => setShowEditModal(false)}
            onSave={handleSaveEdit}
          />
        )}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  contentContainer: {
    width: '100%',
  },
  contentContainerMobile: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.base,
    paddingBottom: Spacing['3xl'],
    backgroundColor: Colors.white,
  },
  fullWidth: {
    width: '100%',
  },

  // Club card
  clubCard: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    overflow: 'hidden',
    marginBottom: Spacing.xl,
    ...Shadows.card,
  },
  clubHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.base,
    padding: Spacing.xl,
    backgroundColor: Colors.background,
  },
  clubHeaderMobile: {
    padding: Spacing.base,
  },
  clubInfo: {
    flex: 1,
    minWidth: 0,
  },
  clubNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  clubName: {
    color: Colors.textDark,
    fontSize: 26,
    lineHeight: 31,
    flexShrink: 1,
  },
  clubNameMobile: {
    fontSize: 22,
    lineHeight: 26,
  },
  verifiedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    backgroundColor: StatusTints.success.bg,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 5,
    borderRadius: Radii.tag,
  },
  verifiedText: {
    fontFamily: Fonts.semiBold,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: StatusTints.success.fg,
  },
  clubDescription: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 21,
    color: Colors.textMedium,
    marginTop: Spacing.xs,
  },
  statsRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: Spacing.sm,
  },
  statCellBordered: {
    borderLeftWidth: 1,
    borderLeftColor: Colors.borderLight,
  },
  statValue: {
    fontFamily: Fonts.bold,
    fontSize: 22,
    lineHeight: 26,
    color: Colors.textDark,
  },
  statLabel: {
    color: Colors.textLight,
    marginTop: 6,
  },
  mobileAction: {
    padding: Spacing.base,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
  },

  // Manager tools
  managerTools: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.base,
    marginBottom: 28,
  },
  managerToolsMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  // Listings
  listingsSection: {},
  listingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.md,
  },
  listingsGridDesktop: {
    gap: Spacing.lg,
  },
  gridItemMobile: {
    width: '48%',
    flexGrow: 1,
    maxWidth: '50%',
  },
  /** Naked listing cards: 4 to a row on desktop (gap 20) */
  gridItemDesktop: {
    width: '23%',
    flexGrow: 1,
    maxWidth: '25%',
    minWidth: 0,
    ...(Platform.OS === 'web' && { flexBasis: 'calc(25% - 15px)', flexGrow: 0 } as any),
  },
  errorText: {
    fontSize: 16,
    color: Colors.error,
  },
});
